import React, { useState, useEffect, useCallback } from 'react';

const API = '/api/intelligence';
function getToken() { return localStorage.getItem('authToken') || ''; }
function h() { return { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }; }

function timeAgo(d: string) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 2) return "à l'instant";
  if (m < 60) return `il y a ${m}min`;
  const hr = Math.floor(m / 60);
  if (hr < 24) return `il y a ${hr}h`;
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function Stars({ n }: { n: number | null }) {
  if (!n) return <span className="text-gray-300 text-xs">—</span>;
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} viewBox="0 0 20 20" className={`w-3 h-3 ${i <= Math.round(n) ? 'text-amber-400' : 'text-gray-200'}`} fill="currentColor">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
        </svg>
      ))}
      <span className="text-xs text-gray-600 font-medium ml-1">{n.toFixed(1)}</span>
    </span>
  );
}

function ScoreBar({ score }: { score: number }) {
  const max = 5;
  const pct = Math.round((score / max) * 100);
  const color = score >= 4 ? 'bg-emerald-500' : score >= 2 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] font-semibold text-gray-500">{score}/{max}</span>
    </div>
  );
}

type Concurrent = {
  id: number;
  nom_commerce: string;
  adresse: string;
  site_web: string | null;
  facebook: string | null;
  instagram: string | null;
  linkedin: string | null;
  tiktok: string | null;
  note_google: number | null;
  score: number;
  is_new: boolean;
  scan_date: string;
  metier: string;
};

type Stats = { total: number; withFb: number; withIg: number; withSite: number; avgNote: string | null; newCount: number };
type NewsItem = { title: string; link: string; pubDate: string; source: string; desc: string };
type TrendItem = { title: string; link: string; type: 'rising' | 'top'; value: string };

export default function IntelligencePage() {
  const [concurrents, setConcurrents] = useState<Concurrent[]>([]);
  const [stats, setStats]             = useState<Stats | null>(null);
  const [lastScan, setLastScan]       = useState<string | null>(null);
  const [scanning, setScanning]       = useState(false);
  const [scanError, setScanError]     = useState<string | null>(null);
  const [news, setNews]               = useState<NewsItem[]>([]);
  const [trends, setTrends]           = useState<TrendItem[]>([]);
  const [trendsKeyword, setTrendsKeyword] = useState('');
  const [newsQuery, setNewsQuery]      = useState('');
  const [inputQ, setInputQ]           = useState('');
  const [loadingNews, setLoadingNews] = useState(false);
  const [loading, setLoading]         = useState(true);

  const loadSaved = useCallback(async () => {
    const [compRes, newsRes, trendsRes] = await Promise.allSettled([
      fetch(`${API}/competitors`, { headers: h() }).then(r => r.json()),
      fetch(`${API}/news`, { headers: h() }).then(r => r.json()),
      fetch(`${API}/trends`, { headers: h() }).then(r => r.json()),
    ]);
    if (compRes.status === 'fulfilled' && compRes.value.success) {
      setConcurrents(compRes.value.concurrents);
      setStats(compRes.value.stats);
      setLastScan(compRes.value.lastScan);
    }
    if (newsRes.status === 'fulfilled' && newsRes.value.success) {
      setNews(newsRes.value.items);
      setNewsQuery(newsRes.value.query);
    }
    if (trendsRes.status === 'fulfilled' && trendsRes.value.success) {
      setTrends(trendsRes.value.trends);
      setTrendsKeyword(trendsRes.value.keyword || '');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  const runScan = async () => {
    setScanning(true);
    setScanError(null);
    try {
      const res = await fetch(`${API}/scan`, { method: 'POST', headers: h() });
      const json = await res.json();
      if (!json.success) {
        setScanError(json.error || 'Erreur lors du scan.');
      } else {
        setConcurrents(json.concurrents);
        setStats(json.stats);
        setLastScan(new Date().toISOString().slice(0, 10));
      }
    } catch {
      setScanError('Erreur réseau.');
    }
    setScanning(false);
  };

  const handleDelete = async (id: number) => {
    await fetch(`${API}/competitors/${id}`, { method: 'DELETE', headers: h() });
    setConcurrents(prev => prev.filter(c => c.id !== id));
  };

  const searchNews = async () => {
    if (!inputQ.trim()) return;
    setLoadingNews(true);
    try {
      const res = await fetch(`${API}/news?q=${encodeURIComponent(inputQ)}`, { headers: h() }).then(r => r.json());
      if (res.success) { setNews(res.items); setNewsQuery(res.query); }
    } finally { setLoadingNews(false); }
  };

  const pct = (n: number) => stats?.total ? Math.round((n / stats.total) * 100) : 0;

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="animate-spin inline-block w-10 h-10 border-4 border-violet-500 border-t-transparent rounded-full mb-4" />
        <p className="text-gray-500 text-sm">Chargement…</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* ── KPIs ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Concurrents détectés', value: stats?.total ?? 0,        grad: 'from-violet-600 to-indigo-600', icon: '🎯' },
          { label: 'Ont un site web',       value: stats?.withSite ?? 0,     grad: 'from-blue-500 to-sky-600',     icon: '🌐', sub: stats ? `${pct(stats.withSite)}%` : null },
          { label: 'Sur Instagram',         value: stats?.withIg ?? 0,       grad: 'from-rose-500 to-pink-600',    icon: '📸', sub: stats ? `${pct(stats.withIg)}%` : null },
          { label: 'Note Google moy.',      value: stats?.avgNote ?? '—',    grad: 'from-amber-500 to-orange-500', icon: '⭐' },
        ].map(k => (
          <div key={k.label} className={`rounded-2xl bg-gradient-to-br ${k.grad} p-5 text-white shadow-md relative overflow-hidden`}>
            <div className="absolute top-3 right-4 text-2xl opacity-25">{k.icon}</div>
            <p className="text-white/70 text-[11px] font-bold uppercase tracking-widest mb-1">{k.label}</p>
            <p className="text-4xl font-black">{k.value}</p>
            {'sub' in k && k.sub && <p className="text-white/60 text-xs mt-0.5">{k.sub} des concurrents</p>}
          </div>
        ))}
      </div>

      {/* ── Scan concurrents ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-violet-700 to-indigo-600 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-white">
              <circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="M21 21l-4.35-4.35"/>
            </svg>
            <div>
              <h3 className="font-bold text-white text-sm leading-none">Concurrents locaux</h3>
              {lastScan && <p className="text-white/50 text-[10px] mt-0.5">Dernier scan : {lastScan}</p>}
            </div>
            {(stats?.newCount ?? 0) > 0 && (
              <span className="bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse">
                {stats!.newCount} nouveau{stats!.newCount > 1 ? 'x' : ''}
              </span>
            )}
          </div>
          <button onClick={runScan} disabled={scanning}
            className="flex items-center gap-2 bg-white text-violet-700 font-bold text-xs px-4 py-2 rounded-xl hover:bg-violet-50 transition-colors shadow disabled:opacity-60">
            {scanning ? (
              <><span className="animate-spin w-3.5 h-3.5 border-2 border-violet-600 border-t-transparent rounded-full" /> Scan en cours…</>
            ) : (
              <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg> Scanner maintenant</>
            )}
          </button>
        </div>

        {scanError && (
          <div className="mx-5 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
            <span>❌</span> {scanError}
          </div>
        )}

        {scanning && (
          <div className="px-5 py-8 text-center">
            <div className="animate-spin inline-block w-8 h-8 border-3 border-violet-500 border-t-transparent rounded-full mb-3" />
            <p className="text-sm font-semibold text-gray-700">Scan Google Places en cours…</p>
            <p className="text-xs text-gray-400 mt-1">Recherche des concurrents, analyse de leurs sites et réseaux sociaux.</p>
            <div className="flex items-center justify-center gap-6 mt-4 text-xs text-gray-400">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-violet-400 rounded-full animate-ping" />Géolocalisation</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-blue-400 rounded-full animate-ping" style={{ animationDelay: '0.3s' }} />Google Places</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-rose-400 rounded-full animate-ping" style={{ animationDelay: '0.6s' }} />Réseaux sociaux</span>
            </div>
          </div>
        )}

        {!scanning && concurrents.length === 0 && !scanError && (
          <div className="text-center py-14 text-gray-400">
            <p className="text-5xl mb-3">🔍</p>
            <p className="text-sm font-semibold text-gray-600">Aucun concurrent trouvé</p>
            <p className="text-xs mt-1 mb-4">Lancez un scan pour découvrir automatiquement vos concurrents locaux.</p>
            <button onClick={runScan}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl shadow hover:from-violet-700 hover:to-indigo-700 transition-all">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4"><circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="M21 21l-4.35-4.35"/></svg>
              Lancer le premier scan
            </button>
          </div>
        )}

        {!scanning && concurrents.length > 0 && (
          <>
            {/* Barre de stats */}
            {stats && (
              <div className="grid grid-cols-3 divide-x divide-gray-100 bg-gray-50 border-b border-gray-100">
                {[
                  { label: 'Avec site web', val: stats.withSite, pct: pct(stats.withSite), color: 'bg-blue-400' },
                  { label: 'Sur Facebook',  val: stats.withFb,   pct: pct(stats.withFb),   color: 'bg-blue-600' },
                  { label: 'Sur Instagram', val: stats.withIg,   pct: pct(stats.withIg),   color: 'bg-pink-500' },
                ].map(s => (
                  <div key={s.label} className="px-5 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500">{s.label}</span>
                      <span className="text-xs font-bold text-gray-700">{s.val} <span className="text-gray-400 font-normal">({s.pct}%)</span></span>
                    </div>
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full ${s.color} rounded-full`} style={{ width: `${s.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Liste */}
            <div className="divide-y divide-gray-50">
              {concurrents.map(c => (
                <div key={c.id} className="flex items-start gap-4 px-5 py-4 hover:bg-gray-50 transition-colors group">
                  {/* Avatar + rang */}
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center font-bold text-violet-700 text-sm shrink-0 border border-violet-200 relative">
                    {c.nom_commerce[0]?.toUpperCase()}
                    {c.is_new && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-rose-500 rounded-full flex items-center justify-center text-white text-[8px] font-black border border-white">N</span>
                    )}
                  </div>

                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900 text-sm">{c.nom_commerce}</p>
                      {c.is_new && <span className="text-[10px] font-bold text-rose-600 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full">Nouveau</span>}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5 truncate">{c.adresse}</p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <Stars n={c.note_google} />
                      <ScoreBar score={c.score} />
                    </div>
                  </div>

                  {/* Liens */}
                  <div className="hidden sm:flex items-center gap-1.5 shrink-0">
                    {c.site_web && (
                      <a href={c.site_web} target="_blank" rel="noreferrer" title="Site web"
                        className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                        <svg viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth={2} className="w-3.5 h-3.5">
                          <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                          <path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
                        </svg>
                      </a>
                    )}
                    {c.facebook && (
                      <a href={c.facebook} target="_blank" rel="noreferrer" title="Facebook"
                        className="w-7 h-7 rounded-lg bg-blue-50 hover:bg-blue-100 flex items-center justify-center transition-colors">
                        <svg viewBox="0 0 24 24" fill="#1877f2" className="w-3.5 h-3.5">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      </a>
                    )}
                    {c.instagram && (
                      <a href={c.instagram} target="_blank" rel="noreferrer" title="Instagram"
                        className="w-7 h-7 rounded-lg bg-pink-50 hover:bg-pink-100 flex items-center justify-center transition-colors">
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" style={{ fill: '#e1306c' }}>
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      </a>
                    )}
                    {c.linkedin && (
                      <a href={c.linkedin} target="_blank" rel="noreferrer" title="LinkedIn"
                        className="w-7 h-7 rounded-lg bg-sky-50 hover:bg-sky-100 flex items-center justify-center transition-colors">
                        <svg viewBox="0 0 24 24" fill="#0077b5" className="w-3.5 h-3.5">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      </a>
                    )}
                    {c.tiktok && (
                      <a href={c.tiktok} target="_blank" rel="noreferrer" title="TikTok"
                        className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                        <svg viewBox="0 0 24 24" fill="#010101" className="w-3.5 h-3.5">
                          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.19 8.19 0 004.79 1.53V6.77a4.85 4.85 0 01-1.02-.08z"/>
                        </svg>
                      </a>
                    )}
                    {!c.site_web && !c.facebook && !c.instagram && (
                      <span className="text-[11px] text-gray-300 italic">Pas en ligne</span>
                    )}
                  </div>

                  {/* Score visible + delete */}
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
                      c.score >= 4 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' :
                      c.score >= 2 ? 'text-amber-700 bg-amber-50 border-amber-200' :
                      'text-gray-500 bg-gray-50 border-gray-200'
                    }`}>
                      Score {c.score}/5
                    </div>
                    <button onClick={() => handleDelete(c.id)}
                      className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 flex items-center justify-center transition-all">
                      <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Actualités + Tendances ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">

        {/* Actualités (2/3) */}
        <div className="xl:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-700 to-sky-600 px-5 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 shrink-0">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-white">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
              </svg>
              <h3 className="font-bold text-white text-sm">Actualités du secteur</h3>
            </div>
            <div className="flex gap-2 flex-1 max-w-xs">
              <input type="text" placeholder={newsQuery || 'Sujet…'} value={inputQ}
                onChange={e => setInputQ(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchNews()}
                className="flex-1 min-w-0 bg-white/20 text-white placeholder-white/50 text-xs px-3 py-1.5 rounded-lg border border-white/25 focus:outline-none focus:bg-white/30" />
              <button onClick={searchNews} disabled={loadingNews}
                className="shrink-0 bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1.5 rounded-lg border border-white/25 transition-colors disabled:opacity-50">
                {loadingNews ? '…' : 'OK'}
              </button>
            </div>
          </div>
          {newsQuery && (
            <div className="px-5 py-2 bg-blue-50 border-b border-blue-100">
              <span className="text-xs text-blue-700 font-medium">Résultats pour : <em>"{newsQuery}"</em></span>
            </div>
          )}
          <div className="divide-y divide-gray-50 max-h-[480px] overflow-y-auto">
            {news.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">Aucun article trouvé.</div>
            ) : news.map((item, i) => (
              <a key={i} href={item.link} target="_blank" rel="noreferrer"
                className="flex gap-3 px-5 py-3 hover:bg-gray-50 transition-colors group block">
                <div className="shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-blue-100 to-sky-100 flex items-center justify-center text-blue-500 font-bold text-[11px] mt-0.5">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 leading-snug line-clamp-2 transition-colors">{item.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {item.source && <span className="text-[11px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">{item.source}</span>}
                    {item.pubDate && <span className="text-[11px] text-gray-400">{timeAgo(item.pubDate)}</span>}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Tendances secteur (1/3) */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-rose-600 to-pink-500 px-5 py-3">
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-white shrink-0">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
              </svg>
              <div>
                <h3 className="font-bold text-white text-sm leading-none">Tendances de votre secteur</h3>
                {trendsKeyword && <p className="text-white/60 text-[10px] mt-0.5 truncate">Basé sur : {trendsKeyword}</p>}
              </div>
            </div>
          </div>

          {/* Legend */}
          {trends.length > 0 && (
            <div className="flex gap-3 px-5 py-2 bg-rose-50 border-b border-rose-100">
              <span className="flex items-center gap-1 text-[10px] text-rose-700 font-semibold">
                <span className="w-2 h-2 rounded-full bg-rose-500 inline-block" /> En hausse
              </span>
              <span className="flex items-center gap-1 text-[10px] text-gray-500 font-semibold">
                <span className="w-2 h-2 rounded-full bg-gray-300 inline-block" /> Populaire
              </span>
            </div>
          )}

          <div className="divide-y divide-gray-50 max-h-[460px] overflow-y-auto">
            {trends.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">Chargement…</div>
            ) : trends.map((t, i) => (
              <a key={i} href={t.link} target="_blank" rel="noreferrer"
                className="flex items-center gap-3 px-5 py-2.5 hover:bg-rose-50 transition-colors group">
                <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black ${
                  i < 3 ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-500'
                }`}>{i + 1}</span>
                <span className="flex-1 text-sm font-medium text-gray-800 group-hover:text-rose-700 truncate">{t.title}</span>
                <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  t.type === 'rising'
                    ? 'text-rose-700 bg-rose-50 border border-rose-200'
                    : 'text-gray-500 bg-gray-50 border border-gray-200'
                }`}>
                  {t.type === 'rising' ? `↑ ${t.value}` : t.value}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
